import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const Users = ({ primary }) => {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [msg, setMsg] = useState({ text: '', type: '' });
  const [form, setForm] = useState({ name: '', email: '', password: '', role: 'lco_admin', phone: '', area: '', lco_id: '' });
  const [editUser, setEditUser] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [selectedTenantId, setSelectedTenantId] = useState('');

  const showMsg = (text, type = 'success') => { setMsg({ text, type }); setTimeout(() => setMsg({ text: '', type: '' }), 4000); };

  // What roles can current user create
  const creatableRoles = {
    superadmin: ['tenant_admin', 'dist_admin', 'sub_dist_admin', 'lco_admin', 'director'],
    tenant_admin: ['dist_admin', 'sub_dist_admin', 'lco_admin', 'director'],
    dist_admin: ['sub_dist_admin'],
    sub_dist_admin: ['lco_admin'],
  };

  const roleLabels = {
    superadmin: '🔴 Super Admin',
    tenant_admin: '🟣 Tenant Admin',
    dist_admin: '🔵 Dist Admin',
    sub_dist_admin: '🟢 Sub-Dist Admin',
    lco_admin: '🟡 LCO Admin',
    director: '👁️ Director (View Only)',
    customer: '👤 Customer'
  };

  const roleColors = {
    superadmin: { bg: '#fef2f2', color: '#dc2626' },
    tenant_admin: { bg: '#f5f3ff', color: '#7c3aed' },
    dist_admin: { bg: '#eff6ff', color: '#2563eb' },
    sub_dist_admin: { bg: '#ecfdf5', color: '#16a34a' },
    lco_admin: { bg: '#fffbeb', color: '#d97706' },
    director: { bg: '#f8fafc', color: '#475569' },
    customer: { bg: '#f8fafc', color: '#94a3b8' }
  };

  const allowedRoles = creatableRoles[user?.role] || [];

  useEffect(() => {
    if (user?.role === 'superadmin') {
      loadTenants();
      setLoading(false);
    } else {
      loadUsers();
    }
  }, []);

  const loadTenants = async () => {
    try {
      const res = await axios.get('/api/tenants');
      setTenants(res.data.tenants || []);
    } catch(e) { console.error(e); }
  };

  const loadUsers = async (tenantId) => {
    try {
      const tid = tenantId !== undefined ? tenantId : selectedTenantId;
      const url = tid ? '/api/users?tenant_id=' + tid : '/api/users';
      const res = await axios.get(url);
      setUsers(res.data.users || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleAdd = async () => {
    if (!form.name || !form.email || !form.password) { showMsg('Name, email and password required', 'error'); return; }
    try {
      await axios.post('/api/users', form);
      showMsg('User created successfully!');
      setShowAdd(false);
      setForm({ name: '', email: '', password: '', role: allowedRoles[0] || 'lco_admin', phone: '', area: '', lco_id: '' });
      loadUsers();
    } catch (e) { showMsg(e.response?.data?.message || 'Failed', 'error'); }
  };

  const handleUpdate = async () => {
    try {
      await axios.put('/api/users/' + editUser._id, editUser);
      showMsg('User updated!');
      setEditUser(null);
      loadUsers();
    } catch (e) { showMsg(e.response?.data?.message || 'Failed', 'error'); }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm('Delete user ' + name + '?')) return;
    try {
      await axios.delete('/api/users/' + id);
      showMsg('User deleted!');
      loadUsers();
    } catch (e) { showMsg(e.response?.data?.message || 'Failed', 'error'); }
  };

  const inpStyle = { width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '13px', boxSizing: 'border-box', outline: 'none' };
  const labelStyle = { fontSize: '11px', color: '#64748b', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '1px', display: 'block', marginBottom: '5px' };
  const card = { background: 'white', borderRadius: '16px', padding: '20px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', border: '1px solid #f1f5f9' };

  // Group users by role
  const groupedUsers = users.reduce((acc, u) => {
    if (!acc[u.role]) acc[u.role] = [];
    acc[u.role].push(u);
    return acc;
  }, {});

  return (
    <div>
      <style>{'@keyframes slideIn { from { opacity:0; transform:translateY(-8px); } to { opacity:1; transform:translateY(0); } }'}</style>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' }}>
        <div>
          <h1 style={{ margin: '0 0 4px', fontSize: '24px', fontWeight: '800', color: '#0f172a' }}>User Management</h1>
          <p style={{ margin: 0, color: '#64748b', fontSize: '14px' }}>Manage staff access and permissions</p>
        </div>
        {allowedRoles.length > 0 && (
          <button onClick={() => { setShowAdd(!showAdd); setForm({ name: '', email: '', password: '', role: allowedRoles[0], phone: '', area: '', lco_id: '' }); }}
            style={{ padding: '10px 20px', background: showAdd ? '#ef4444' : primary, color: 'white', border: 'none', borderRadius: '10px', cursor: 'pointer', fontSize: '14px', fontWeight: '700' }}>
            {showAdd ? '✕ Cancel' : '+ Add User'}
          </button>
        )}
      </div>

      {/* Tenant Filter for Superadmin */}
      {user?.role === 'superadmin' && (
        <div style={{ background: 'white', borderRadius: '12px', padding: '16px 20px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', border: '1px solid #f1f5f9', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span style={{ fontSize: '14px', fontWeight: '600', color: '#475569' }}>Filter by Tenant:</span>
          <select value={selectedTenantId} onChange={e => { setSelectedTenantId(e.target.value); loadUsers(e.target.value); }}
            style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '14px', minWidth: '200px' }}>
            <option value=''>-- All Tenants --</option>
            {tenants.map(t => <option key={t._id} value={t._id}>{t.name} ({t.code})</option>)}
          </select>
          {selectedTenantId && (
            <button onClick={() => { setSelectedTenantId(''); loadUsers(''); }}
              style={{ padding: '8px 12px', background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px' }}>
              ✕ Clear Filter
            </button>
          )}
        </div>
      )}

      {/* Message */}
      {msg.text && (
        <div style={{ background: msg.type === 'error' ? '#fef2f2' : '#ecfdf5', border: `1px solid ${msg.type === 'error' ? '#fecaca' : '#bbf7d0'}`, color: msg.type === 'error' ? '#dc2626' : '#16a34a', padding: '12px 16px', borderRadius: '10px', marginBottom: '16px', fontSize: '14px', fontWeight: '500', animation: 'slideIn 0.2s ease' }}>
          {msg.type === 'error' ? '⚠️' : '✅'} {msg.text}
        </div>
      )}

      {/* Add User Form */}
      {showAdd && (
        <div style={{ ...card, marginBottom: '24px', animation: 'slideIn 0.2s ease' }}>
          <h3 style={{ margin: '0 0 20px', fontSize: '16px', fontWeight: '700', color: '#0f172a' }}>👤 Add New User</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
            <div>
              <label style={labelStyle}>Full Name *</label>
              <input style={inpStyle} value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Full name" />
            </div>
            <div>
              <label style={labelStyle}>Role *</label>
              <select style={inpStyle} value={form.role} onChange={e => setForm({ ...form, role: e.target.value })}>
                {allowedRoles.map(r => <option key={r} value={r}>{roleLabels[r]}</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>
                {form.role === 'lco_admin' ? 'LCO ID (used for login) *' : 'Email *'}
              </label>
              <input style={inpStyle} value={form.email}
                onChange={e => setForm({ ...form, email: e.target.value, lco_id: form.role === 'lco_admin' ? e.target.value : form.lco_id })}
                placeholder={form.role === 'lco_admin' ? 'e.g. LCO001 or phone number' : 'email@example.com'} />
            </div>
            <div>
              <label style={labelStyle}>Password *</label>
              <input style={inpStyle} type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} placeholder="Min 6 characters" />
            </div>
            <div>
              <label style={labelStyle}>Phone Number</label>
              <input style={inpStyle} value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} placeholder="10-digit mobile" />
            </div>
            <div>
              <label style={labelStyle}>Area / Location</label>
              <input style={inpStyle} value={form.area} onChange={e => setForm({ ...form, area: e.target.value })} placeholder="Area / Location" />
            </div>
            <div>
              <label style={labelStyle}>Pincode</label>
              <input style={inpStyle} value={form.pincode} onChange={e => setForm({ ...form, pincode: e.target.value })} placeholder="6-digit pincode" maxLength="6" />
            </div>
          </div>

          {/* LCO specific info */}
          {form.role === 'lco_admin' && (
            <div style={{ marginTop: '14px', padding: '12px', background: '#fffbeb', borderRadius: '8px', border: '1px solid #fde68a', fontSize: '13px', color: '#92400e' }}>
              💡 LCO ID will be used as login username. Use a simple ID like their phone number or a code (e.g. LCO001, 9995495969).
              Password should be simple for LCO staff to remember.
            </div>
          )}

          {/* Director info */}
          {form.role === 'director' && (
            <div style={{ marginTop: '14px', padding: '12px', background: '#f0f9ff', borderRadius: '8px', border: '1px solid #bae6fd', fontSize: '13px', color: '#0369a1' }}>
              👁️ Director has VIEW ONLY access. Can see all devices and reports but cannot make any changes.
            </div>
          )}

          <div style={{ display: 'flex', gap: '10px', marginTop: '18px' }}>
            <button onClick={handleAdd}
              style={{ padding: '11px 24px', background: primary, color: 'white', border: 'none', borderRadius: '10px', cursor: 'pointer', fontWeight: '700', fontSize: '14px' }}>
              💾 Create User
            </button>
            <button onClick={() => setShowAdd(false)}
              style={{ padding: '11px 20px', background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: '10px', cursor: 'pointer', fontWeight: '600' }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {editUser && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000, backdropFilter: 'blur(4px)' }}>
          <div style={{ background: 'white', borderRadius: '20px', padding: '32px', width: '480px', boxShadow: '0 24px 60px rgba(0,0,0,0.3)', animation: 'slideIn 0.2s ease' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: '#0f172a' }}>✏️ Edit User</h3>
              <button onClick={() => setEditUser(null)} style={{ background: '#f1f5f9', border: 'none', borderRadius: '8px', width: '32px', height: '32px', cursor: 'pointer', fontSize: '16px' }}>✕</button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div style={{ gridColumn: '1/-1' }}>
                <label style={labelStyle}>Full Name</label>
                <input style={inpStyle} value={editUser.name || ''} onChange={e => setEditUser({ ...editUser, name: e.target.value })} />
              </div>
              <div>
                <label style={labelStyle}>Role</label>
                <select style={inpStyle} value={editUser.role || ''} onChange={e => setEditUser({ ...editUser, role: e.target.value })}>
                  {Object.entries(roleLabels).filter(([r]) => r !== 'superadmin' && r !== 'customer').map(([r, label]) => (
                    <option key={r} value={r}>{label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label style={labelStyle}>Tenant</label>
                <select style={inpStyle} value={editUser.tenant_id || ''} onChange={e => setEditUser({ ...editUser, tenant_id: e.target.value })}>
                  <option value=''>-- Select Tenant --</option>
                  {tenants.map(t => <option key={t._id} value={t._id}>{t.name} ({t.code})</option>)}
                </select>
              </div>
              <div>
                <label style={labelStyle}>Email Address</label>
                <input style={inpStyle} type="email" value={editUser.email || ''} onChange={e => setEditUser({ ...editUser, email: e.target.value })} placeholder="email@example.com" />
              </div>
              <div>
                <label style={labelStyle}>Phone</label>
                <input style={inpStyle} value={editUser.phone || ''} onChange={e => setEditUser({ ...editUser, phone: e.target.value })} />
              </div>
              <div>
                <label style={labelStyle}>Area</label>
                <input style={inpStyle} value={editUser.area || ''} onChange={e => setEditUser({ ...editUser, area: e.target.value })} />
              </div>
              <div>
                <label style={labelStyle}>Pincode</label>
                <input style={inpStyle} value={editUser.pincode || ''} onChange={e => setEditUser({ ...editUser, pincode: e.target.value })} placeholder="6-digit pincode" maxLength="6" />
              </div>
              <div>
                <label style={labelStyle}>New Password (leave blank to keep)</label>
                <input style={inpStyle} type="password" value={editUser.newPassword || ''} onChange={e => setEditUser({ ...editUser, password: e.target.value })} placeholder="New password" />
              </div>
              <div>
                <label style={labelStyle}>Status</label>
                <select style={inpStyle} value={editUser.is_active ? 'true' : 'false'} onChange={e => setEditUser({ ...editUser, is_active: e.target.value === 'true' })}>
                  <option value="true">🟢 Active</option>
                  <option value="false">🔴 Inactive</option>
                </select>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
              <button onClick={handleUpdate} style={{ flex: 1, padding: '12px', background: primary, color: 'white', border: 'none', borderRadius: '10px', cursor: 'pointer', fontWeight: '700', fontSize: '14px' }}>💾 Save Changes</button>
              <button onClick={() => setEditUser(null)} style={{ padding: '12px 20px', background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: '10px', cursor: 'pointer', fontWeight: '600' }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Users List - Grouped by Role */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#94a3b8' }}>Loading users...</div>
      ) : users.length === 0 ? (
        <div style={{ ...card, textAlign: 'center', padding: '60px', color: '#94a3b8' }}>
          <div style={{ fontSize: '48px', marginBottom: '12px' }}>👥</div>
          <div style={{ fontSize: '16px', fontWeight: '600', color: '#475569' }}>No users found</div>
          <div style={{ fontSize: '14px', marginTop: '4px' }}>Click "+ Add User" to create the first user</div>
        </div>
      ) : (
        Object.entries(groupedUsers).map(([role, roleUsers]) => (
          <div key={role} style={{ ...card, marginBottom: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
              <span style={{ background: roleColors[role]?.bg || '#f8fafc', color: roleColors[role]?.color || '#475569', padding: '4px 12px', borderRadius: '20px', fontSize: '13px', fontWeight: '700' }}>
                {roleLabels[role] || role}
              </span>
              <span style={{ fontSize: '12px', color: '#94a3b8' }}>{roleUsers.length} user{roleUsers.length > 1 ? 's' : ''}</span>
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f8fafc' }}>
                  {['Name', 'Login ID', 'Phone', 'Area', 'Pincode', 'Status', 'Created', 'Actions'].map(h => (
                    <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontSize: '11px', color: '#64748b', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '1px solid #f1f5f9' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {roleUsers.map(u => (
                  <tr key={u._id} style={{ borderBottom: '1px solid #f8fafc' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                    onMouseLeave={e => e.currentTarget.style.background = 'white'}>
                    <td style={{ padding: '12px 14px' }}>
                      <div style={{ fontWeight: '600', fontSize: '14px', color: '#1e293b' }}>{u.name}</div>
                    </td>
                    <td style={{ padding: '12px 14px' }}>
                      <div style={{ fontFamily: 'monospace', fontSize: '12px', color: '#475569', background: '#f1f5f9', padding: '2px 8px', borderRadius: '6px', display: 'inline-block' }}>
                        {u.lco_id || u.email}
                      </div>
                      {u.lco_id && (
                        <div style={{ fontSize: '10px', color: '#94a3b8', marginTop: '2px' }}>{u.email}</div>
                      )}
                    </td>
                    <td style={{ padding: '12px 14px', fontSize: '13px', color: '#374151' }}>{u.phone || '—'}</td>
                    <td style={{ padding: '12px 14px', fontSize: '13px', color: '#374151' }}>{u.area || '—'}</td>
                    <td style={{ padding: '12px 14px', fontSize: '13px', color: '#374151' }}>{u.pincode || '—'}</td>
                    <td style={{ padding: '12px 14px' }}>
                      <span style={{ background: u.is_active ? '#ecfdf5' : '#fef2f2', color: u.is_active ? '#16a34a' : '#dc2626', padding: '3px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: '700' }}>
                        {u.is_active ? '● Active' : '● Inactive'}
                      </span>
                    </td>
                    <td style={{ padding: '12px 14px', fontSize: '12px', color: '#94a3b8' }}>
                      {new Date(u.createdAt).toLocaleDateString('en-IN')}
                    </td>
                    <td style={{ padding: '12px 14px' }}>
                      <div style={{ display: 'flex', gap: '6px' }}>
                        <button onClick={() => setEditUser({ ...u })}
                          style={{ padding: '5px 10px', background: '#eff6ff', color: '#2563eb', border: 'none', borderRadius: '7px', cursor: 'pointer', fontSize: '12px', fontWeight: '700' }}>
                          ✏️ Edit
                        </button>
                        {u._id !== user.id && (
                          <button onClick={() => handleDelete(u._id, u.name)}
                            style={{ padding: '5px 10px', background: '#fef2f2', color: '#dc2626', border: 'none', borderRadius: '7px', cursor: 'pointer', fontSize: '12px', fontWeight: '700' }}>
                            🗑️
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))
      )}
    </div>
  );
};

export default Users;
